import {
  INITIAL_YOUTUBE_EMBED_STATE,
  isYoutubeEmbedInitialDelivery,
  mergeYoutubeEmbedInfo,
  parseYoutubeEmbedMessage,
  seekYoutubeEmbedState,
  YOUTUBE_EMBED_ORIGIN,
  youtubeEmbedCommandMessage,
  youtubeEmbedListeningMessage,
  youtubeEmbedSample,
  type YoutubeEmbedCommand,
  type YoutubeEmbedSample,
  type YoutubeEmbedState,
} from '@visious/core';
import { systemClock } from '../platform/clock';

/**
 * The page's side of the YouTube embed's message API (core's `youtubeembed/bridge.ts` has the
 * protocol). One per iframe: it listens for that frame's reports, keeps the folded state, and sends
 * commands into it. Nothing here touches YouTube's chrome — the embed stays YouTube's own player.
 */
export interface YoutubeEmbedBridge {
  /** The transport's sample, or `null` while there is none to trust (see `youtubeEmbedSample`). */
  sample(expected: { readonly videoId: string; readonly durationSec: number | null }): YoutubeEmbedSample | null;
  send(command: YoutubeEmbedCommand): void;
  destroy(): void;
}

/** How often the handshake is repeated. YouTube's own script's cadence. */
const LISTEN_EVERY_MS = 250;

type Expected = Parameters<YoutubeEmbedBridge['sample']>[0];

export function connectYoutubeEmbed(frame: HTMLIFrameElement): YoutubeEmbedBridge {
  let state: YoutubeEmbedState = INITIAL_YOUTUBE_EMBED_STATE;
  /** The video the page last sampled for — what a reload's resume point is checked against. */
  let expected: Expected | null = null;

  const post = (message: string): void => {
    frame.contentWindow?.postMessage(message, YOUTUBE_EMBED_ORIGIN);
  };
  const send = (command: YoutubeEmbedCommand): void => {
    post(youtubeEmbedCommandMessage(command));
    if (command.kind === 'seek') state = seekYoutubeEmbedState(state, command.timeSec, systemClock.nowEpochMs());
  };

  const onMessage = (event: MessageEvent): void => {
    // Both checks: the origin says it is YouTube, the source says it is *this* frame — a second
    // embed elsewhere on the page would otherwise be read as this one.
    if (event.origin !== YOUTUBE_EMBED_ORIGIN || event.source !== frame.contentWindow) return;
    const info = parseYoutubeEmbedMessage(event.data);
    if (info === null) return;
    /*
     * A first report after the page already had a position is a new document starting over from the
     * address's `start`. The dock moves this frame between the watch page and the corner player, and
     * where the browser has no `moveBefore` that move reloads it (`playback/dock.ts`). So the position
     * and the play state from just before are put back. The sample is the transport's own, so an ad
     * on screen at that moment leaves the embed where it restarts.
     */
    const resume =
      expected !== null && isYoutubeEmbedInitialDelivery(event.data)
        ? youtubeEmbedSample(state, systemClock.nowEpochMs(), expected)
        : null;
    state = mergeYoutubeEmbedInfo(state, info, systemClock.nowEpochMs());
    if (resume !== null) {
      send({ kind: 'seek', timeSec: resume.currentTimeSec });
      send({ kind: resume.paused ? 'pause' : 'play' });
    }
  };
  globalThis.addEventListener('message', onMessage);

  /*
   * The handshake is repeated for the bridge's whole life, and again on every `load` of the frame —
   * what YouTube's own `iframe_api` script does. Sending it only until the first answer (the first
   * version, 2026-10-09) worked in Chrome and left Firefox silent: there the embed's document can be
   * replaced after it has answered, and the new one never hears a handshake, so it never reports.
   * The player's listener exists only once its script has run, which is after any `load` the page
   * could wait for, so a one-off send on `load` would not do either. One message every 250 ms.
   */
  const listen = (): void => post(youtubeEmbedListeningMessage());
  const timer = globalThis.setInterval(listen, LISTEN_EVERY_MS);
  frame.addEventListener('load', listen);

  return {
    sample: (video) => {
      expected = video;
      return youtubeEmbedSample(state, systemClock.nowEpochMs(), video);
    },
    send,
    destroy: () => {
      globalThis.clearInterval(timer);
      frame.removeEventListener('load', listen);
      globalThis.removeEventListener('message', onMessage);
    },
  };
}
